import { useState } from "react";
import { UncontrolledPopover, PopoverBody } from "reactstrap";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { CancelOrderModal } from "./CancelOrderModal";
import { UpdateStatusOrderModal } from "./UpdateStatusOrderModal";
import { getShortId } from "../../utils/getShortId";

export const OrderActionsCell = ({ order, showDetail = true }) => {
  const navigate = useNavigate();
  const { patchData, invalidateQuery } = useDataFetch();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [shippingTracking, setShippingTracking] = useState("");
  const [shippingStatus, setShippingStatus] = useState("");
  const [shippingDescription, setShippingDescription] = useState("");

  const { mutateAsync: cancelOrder } = patchData(`/order/cancel`, {}, {
    onSuccess: () => {
      toast.success("Orden cancelada correctamente");
      setCancelModalOpen(false);
      setCancelReason("");
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("No se pudo cancelar la orden"),
  });

  const { mutateAsync: updateShippingStatus } = patchData(`/order/status`, {}, {
    onSuccess: () => {
      toast.success("Estado actualizado correctamente");
      setShippingModalOpen(false);
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al actualizar estado de envío"),
  });

  const { mutateAsync: markDelivered } = patchData(`/order/markDelivered`, {}, {
    onSuccess: () => {
      toast.success("Orden marcada como entregada");
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al marcar como entregada"),
  });

  const { mutateAsync: markPaid } = patchData(`/order/markPaid`, {}, {
    onSuccess: () => {
      toast.success("Orden marcada como pagada");
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al marcar como pagada"),
  });

  const handleDeliveredClick = () => {
    Swal.fire({
      title: "¿Marcar como entregada?",
      text: `Confirmas que la orden ${getShortId(order.id)}... fue entregada.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, marcar como entregada",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0F9A5A",
    }).then(async (result) => {
      if (result.isConfirmed) await markDelivered({ id: order.id });
    });
  };

  const handleCancelClick = () => setCancelModalOpen(true);
  const handleShippingClick = () => setShippingModalOpen(true);

  const handleMarkPaidClick = () => {
    Swal.fire({
      title: "¿Marcar como pagada?",
      text: `Confirmas que la orden ${getShortId(order.id)}... fue pagada por transferencia.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, marcar como pagada",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0F9A5A",
    }).then(async (result) => {
      if (result.isConfirmed) await markPaid({ id: order.id });
    });
  };

  const { id, status, payment_method, delivery_type } = order;
  const isDelivered = status === "DELIVERED";
  const isPaid = status === "PAID";
  const isTransfer = payment_method === "TRANSFER";
  const isStorePickup = delivery_type === "STORE_PICKUP";
  const isDelivery = delivery_type === "DELIVERY";

  const showCancel = !isDelivered && status !== "CANCELLED";
  const showShipping = isDelivery && (isPaid || status === "PROCESSING");
  const showMarkPaid = isTransfer && (status === "PENDING_APPROVAL" || status === "PENDING_PROOF");
  const showMarkDelivered =
    !isDelivered &&
    (status === "SHIPPED" ||
      (status === "PENDING_APPROVAL" && isStorePickup && !isTransfer) ||
      isPaid);

  const hasMenu = showCancel || showShipping || showMarkPaid || showMarkDelivered;
  const menuId = `order-menu-${id}`;

  return (
    <>
      <div className="d-flex gap-2 justify-content-end">
        {showDetail && (
          <button
            type="button"
            className="ui-icon-btn ui-icon-btn--accent"
            onClick={() => navigate(`/order/detail/${id}`)}
            title="Ver detalle"
          >
            <i className="ri-eye-line" />
          </button>
        )}

        {hasMenu && (
          <>
            <button type="button" id={menuId} className="ui-icon-btn">
              <i className="ri-more-2-fill" />
            </button>
            <UncontrolledPopover trigger="legacy" placement="bottom-end" target={menuId}>
              <PopoverBody className="ui-menu">
                {showMarkPaid && (
                  <button type="button" className="ui-menu__item" onClick={handleMarkPaidClick}>
                    <i className="ri-checkbox-circle-line" /> Marcar como pagada
                  </button>
                )}
                {showShipping && (
                  <button type="button" className="ui-menu__item" onClick={handleShippingClick}>
                    <i className="ri-truck-line" /> Actualizar envío
                  </button>
                )}
                {showMarkDelivered && (
                  <button type="button" className="ui-menu__item" onClick={handleDeliveredClick}>
                    <i className="ri-checkbox-circle-line" /> Marcar como entregada
                  </button>
                )}
                {showCancel && (
                  <button type="button" className="ui-menu__item ui-menu__item--danger" onClick={handleCancelClick}>
                    <i className="ri-close-circle-line" /> Cancelar orden
                  </button>
                )}
              </PopoverBody>
            </UncontrolledPopover>
          </>
        )}
      </div>

      <CancelOrderModal
        cancelModalOpen={cancelModalOpen}
        setCancelModalOpen={setCancelModalOpen}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        selectedOrder={order}
        cancelOrder={cancelOrder}
      />

      <UpdateStatusOrderModal
        shippingModalOpen={shippingModalOpen}
        setShippingModalOpen={setShippingModalOpen}
        shippingStatus={shippingStatus}
        setShippingStatus={setShippingStatus}
        shippingDescription={shippingDescription}
        setShippingDescription={setShippingDescription}
        shippingTracking={shippingTracking}
        setShippingTracking={setShippingTracking}
        selectedOrder={order}
        updateShippingStatus={updateShippingStatus}
      />
    </>
  );
};
